/**
 * Shareable program join links.
 *
 * A brand can run several programs but only one is the default. Rather than
 * assigning affiliates to the extra programs by hand, the brand shares a link
 * that names the program with an opaque token:
 *
 *   /store/<username>/join?p=<shareToken>        (public, logged out)
 *   /affiliate/brands/<username>?p=<shareToken>  (inside the dashboard)
 *
 * The token is `Program.shareToken` from the API — never the program id, and
 * never anything guessable. No token, or a token the API can't resolve, means
 * "show the default program", which is the pre-share-link behaviour.
 */

export const PROGRAM_TOKEN_PARAM = "p";

/** Read the program token off a location.search / URLSearchParams. */
export function readProgramToken(
  search: string | URLSearchParams
): string | undefined {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  return params.get(PROGRAM_TOKEN_PARAM)?.trim() || undefined;
}

/** Append `?p=<token>` to a path, leaving any existing query intact. */
export function withProgramToken(path: string, token?: string): string {
  if (!token) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${PROGRAM_TOKEN_PARAM}=${encodeURIComponent(
    token
  )}`;
}

/** The public join-portal URL a brand copies and shares. */
export function buildJoinPortalUrl(username: string, token?: string): string {
  return withProgramToken(
    `${window.location.origin}/store/${username}/join`,
    token
  );
}

/**
 * Drop the token from the current URL.
 *
 * Used after the API reports the token is stale: without this, every reload
 * re-fires the "program not found" toast and the join request would keep
 * sending a token the backend has already rejected.
 */
export function stripProgramTokenFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(PROGRAM_TOKEN_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
